import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faPlus,
  faTrash,
  faToggleOn,
  faToggleOff,
  faProjectDiagram
} from '@fortawesome/free-solid-svg-icons';
import { WorkflowService, sharedApiClient } from '../fetcher';
import { IdentityService, TeamService, UserAbility, useNotifications, useAuth, MAIN_TAB_PAGE_LABEL, medPromptBrowserTitle } from '@medprompt/shared';
import { Table, createTextColumn, createEnumColumn, createCustomColumn, useTableState } from '@medprompt/shared';
import WorkflowPreviewModal from './components/WorkflowPreviewModal';
import CreateWorkflowModal from './components/CreateWorkflowModal';
import HelpButton from '../help/HelpButton';
import { HELP_PAGE_IDS } from '../help/helpContent';
import '@medprompt/shared/src/common/modals.css';
import { StatusBadge } from '@medprompt/shared';
import './WorkflowView.css';
import '../list-view-page.css';
import '@medprompt/shared/src/common/TabPageTitle.css';

// ── Bulk Delete Modal ──────────────────────────────────────────
const BulkDeleteWorkflowsModal = ({ isOpen, onClose, onConfirm, workflows, loading }) => {
  if (!isOpen || !workflows || workflows.length === 0) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-animated">
        <div className="modal-header">
          <h2>Delete Workflows</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faSpinner} />
          </button>
        </div>
        <div className="modal-content">
          <p>Are you sure you want to delete {workflows.length} workflow{workflows.length > 1 ? 's' : ''}?</p>
          <div className="bulk-document-list">
            {workflows.slice(0, 5).map(w => (
              <div key={w.ruleId} className="bulk-document-item">
                <div className="bulk-document-name">{w.name || 'Unnamed Workflow'}</div>
                <div className="bulk-document-assignee">
                  {w.conditions?.length || 0} condition(s), {w.actions?.length || 0} action(s)
                </div>
              </div>
            ))}
            {workflows.length > 5 && (
              <div className="bulk-document-more">
                ... and {workflows.length - 5} more workflow{workflows.length - 5 > 1 ? 's' : ''}
              </div>
            )}
          </div>
          <p style={{ marginTop: '15px', color: '#d32f2f', fontSize: '14px', fontWeight: 'bold' }}>
            This action cannot be undone.
          </p>
        </div>
        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button className="header-button header-button-delete" onClick={onConfirm} disabled={loading}>
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Delete All'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Bulk Toggle Active Modal ──────────────────────────────────────────
const BulkToggleActiveModal = ({ isOpen, onClose, onConfirm, workflows, loading, activating }) => {
  if (!isOpen || !workflows || workflows.length === 0) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-animated">
        <div className="modal-header">
          <h2>{activating ? 'Activate' : 'Deactivate'} Workflows</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faSpinner} />
          </button>
        </div>
        <div className="modal-content">
          <p>
            Are you sure you want to {activating ? 'activate' : 'deactivate'} {workflows.length} workflow{workflows.length > 1 ? 's' : ''}?
          </p>
          <div className="bulk-document-list">
            {workflows.slice(0, 5).map(w => (
              <div key={w.ruleId} className="bulk-document-item">
                <div className="bulk-document-name">{w.name || 'Unnamed Workflow'}</div>
              </div>
            ))}
            {workflows.length > 5 && (
              <div className="bulk-document-more">
                ... and {workflows.length - 5} more workflow{workflows.length - 5 > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button className="header-button header-button-primary" onClick={onConfirm} disabled={loading}>
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : (activating ? 'Activate All' : 'Deactivate All')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Controller ────────────────────────────────────────────
const WorkflowViewController = () => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();

  const hasConfigWrite = user?.authorities?.includes(UserAbility.CONFIG_WRITE.key);

  const workflowManager = useMemo(() => new WorkflowService(sharedApiClient), []);
  const refreshingRef = useRef(false);

  const [workflows, setWorkflows] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Bulk selection state
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [workflowsForBulkDelete, setWorkflowsForBulkDelete] = useState([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkToggleModalOpen, setBulkToggleModalOpen] = useState(false);
  const [workflowsForBulkToggle, setWorkflowsForBulkToggle] = useState([]);
  const [bulkToggleLoading, setBulkToggleLoading] = useState(false);
  const [bulkToggleActivating, setBulkToggleActivating] = useState(false);

  const {
    sortState: tableSortState,
    setSortState: setTableSortState,
    filterValues: tableFilterValues,
    setFilterValues: setTableFilterValues,
    disabledFilters: tableDisabledFilters,
    setDisabledFilters: setTableDisabledFilters,
    paginationState: tablePaginationState,
    setPaginationState: setTablePaginationState,
    highlightedRows,
    setHighlightedRows
  } = useTableState('WorkflowsTable', {
    defaultSortState: { columnKey: 'name', direction: 'asc' }
  });

  useEffect(() => {
    document.title = medPromptBrowserTitle(MAIN_TAB_PAGE_LABEL.configuration + ' - Workflows');
  }, []);

  // Fetch users and teams for name resolution
  useEffect(() => {
    const identityManager = new IdentityService();
    const teamManager = new TeamService();
    
    identityManager.fetchActiveClinicUsers()
      .then((users) => setActiveUsers(users || []))
      .catch((error) => {
        console.error('Failed to fetch active users:', error);
        setActiveUsers([]);
      });
    
    teamManager.fetchTeams()
      .then((teamsData) => setTeams(teamsData || []))
      .catch((error) => {
        console.error('Failed to fetch teams:', error);
        setTeams([]);
      });
  }, []);

  const fetchWorkflows = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;

    const isInitialLoad = workflows.length === 0;
    if (isInitialLoad) setLoading(true);

    try {
      const data = await workflowManager.fetchWorkflowRules();
      setWorkflows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
      addNotification('fetch-workflows-error', 'Failed to fetch workflows', { isError: true });
    } finally {
      if (isInitialLoad) setLoading(false);
      refreshingRef.current = false;
    }
  }, [workflows.length, workflowManager, addNotification]);

  useEffect(() => {
    fetchWorkflows();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRowClick = (workflow) => {
    setSelectedWorkflow(workflow);
    setPreviewModalOpen(true);
    setHighlightedRows(new Set([workflow.ruleId]));
  };

  const handleCreateWorkflow = () => {
    setCreateModalOpen(true);
  };

  const handleCreateSuccess = () => {
    fetchWorkflows();
  };

  const handleEditWorkflow = (ruleId) => {
    console.log('[WorkflowViewController] Editing workflow:', ruleId, 'navigating to /workflow/' + ruleId);
    navigate(`/workflow/${ruleId}`);
  };

  // ── Bulk handlers ─────────────────────────────────────────────────
  const handleBulkDelete = useCallback((selectedWorkflowsData) => {
    if (selectedWorkflowsData.length === 0) return;
    setWorkflowsForBulkDelete(selectedWorkflowsData);
    setBulkDeleteModalOpen(true);
  }, []);

  const confirmBulkDelete = () => {
    if (workflowsForBulkDelete.length === 0) return;
    setBulkDeleteLoading(true);

    const promises = workflowsForBulkDelete.map(w =>
      workflowManager.deleteWorkflowRule(w.ruleId)
        .then(() => addNotification(`bulk-delete-${w.ruleId}`, `Workflow "${w.name}" deleted`))
        .catch(() => addNotification(`bulk-delete-err-${w.ruleId}`, `Failed to delete workflow "${w.name}"`, { isError: true }))
    );

    Promise.allSettled(promises).then(() => {
      setSelectedRows(new Set());
      setBulkDeleteModalOpen(false);
      setWorkflowsForBulkDelete([]);
      fetchWorkflows();
    }).finally(() => {
      setBulkDeleteLoading(false);
    });
  };

  const handleBulkToggleActive = useCallback((selectedWorkflowsData, activating) => {
    if (selectedWorkflowsData.length === 0) return;
    
    // Filter workflows that need to be changed
    const workflowsToToggle = selectedWorkflowsData.filter(w => w.isActive !== activating);
    
    if (workflowsToToggle.length === 0) {
      addNotification('bulk-toggle-none', `All selected workflows are already ${activating ? 'active' : 'inactive'}`, { isError: true });
      return;
    }
    
    setWorkflowsForBulkToggle(workflowsToToggle);
    setBulkToggleActivating(activating);
    setBulkToggleModalOpen(true);
  }, [addNotification]);

  const confirmBulkToggle = () => {
    if (workflowsForBulkToggle.length === 0) return;
    setBulkToggleLoading(true);

    const promises = workflowsForBulkToggle.map(w =>
      workflowManager.updateWorkflowRule({
        ruleId: w.ruleId,
        activate: bulkToggleActivating,
        deactivate: !bulkToggleActivating
      })
        .then(() => addNotification(`bulk-toggle-${w.ruleId}`, `Workflow "${w.name}" ${bulkToggleActivating ? 'activated' : 'deactivated'}`))
        .catch(() => addNotification(`bulk-toggle-err-${w.ruleId}`, `Failed to toggle workflow "${w.name}"`, { isError: true }))
    );

    Promise.allSettled(promises).then(() => {
      setSelectedRows(new Set());
      setBulkToggleModalOpen(false);
      setWorkflowsForBulkToggle([]);
      fetchWorkflows();
    }).finally(() => {
      setBulkToggleLoading(false);
    });
  };

  // ── Table columns ────────────────────────────────────────────
  const columns = useMemo(() => [
    createTextColumn('name', 'Name', (w) => w.name || 'Unnamed Workflow'),

    createEnumColumn(
      'event',
      'Event',
      (w) => w.event,
      ['DOCUMENT_COMPLETED'],
      {
        renderer: (value) => {
          const labels = {
            'DOCUMENT_COMPLETED': 'Document Completed'
          };
          return labels[value] || value;
        },
        sortable: true
      }
    ),

    createCustomColumn(
      'conditions',
      'Conditions',
      (w) => w.conditions?.length || 0,
      (count) => <span className="workflow-count">{count}</span>,
      { sortable: true, filterable: false }
    ),

    createCustomColumn(
      'actions',
      'Actions',
      (w) => w.actions?.length || 0,
      (count) => <span className="workflow-count">{count}</span>,
      { sortable: true, filterable: false }
    ),

    createEnumColumn(
      'status',
      'Status',
      (w) => w.isActive ? 'ACTIVE' : 'INACTIVE',
      ['ACTIVE', 'INACTIVE'],
      {
        renderer: (value) => (
          <StatusBadge
            label={value === 'ACTIVE' ? 'Active' : 'Inactive'}
            colorFamily={value === 'ACTIVE' ? 'green' : 'yellow'}
            size="normal"
          />
        ),
        sortable: true,
        compareFn: (a, b) => {
          if (a.isActive === b.isActive) return 0;
          return a.isActive ? -1 : 1;
        }
      }
    )
  ], []);

  const bulkActions = useMemo(() => [
    {
      key: 'activate',
      label: 'Activate',
      icon: faToggleOn,
      onClick: (selectedWorkflowsData) => handleBulkToggleActive(selectedWorkflowsData, true)
    },
    {
      key: 'deactivate',
      label: 'Deactivate',
      icon: faToggleOff,
      onClick: (selectedWorkflowsData) => handleBulkToggleActive(selectedWorkflowsData, false)
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: faTrash,
      onClick: handleBulkDelete
    }
  ], [handleBulkDelete, handleBulkToggleActive]);

  return (
    <div className="workflow-view list-view-page">
      <div className="header">
        <div className="header-title-text">
          <div className="header-title-with-help">
            <h1 className="tab-page-title">
              Workflows
            </h1>
            <HelpButton pageId={HELP_PAGE_IDS.workflows} />
          </div>
        </div>
        <div className="header-buttons">
          {hasConfigWrite && (
            <button
              className="header-button header-button-primary"
              onClick={handleCreateWorkflow}
            >
              <FontAwesomeIcon icon={faPlus} /> Create Workflow
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>Loading workflows...</p>
        </div>
      ) : (
        <div className="workflow-table-container">
          <Table
            id="WorkflowsTable"
            data={workflows}
            columns={columns}
            getRowKey={(w) => w.ruleId}
            sortState={tableSortState}
            onSortChange={setTableSortState}
            filterValues={tableFilterValues}
            onFilterChange={setTableFilterValues}
            disabledFilters={tableDisabledFilters}
            onDisabledFiltersChange={setTableDisabledFilters}
            paginationState={tablePaginationState}
            onPaginationChange={setTablePaginationState}
            onRowClick={handleRowClick}
            enableSelection={hasConfigWrite}
            bulkActions={bulkActions}
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            highlightedRows={highlightedRows}
            onHighlightChange={setHighlightedRows}
            emptyMessage="No workflows found. Create your first workflow to get started."
          />
        </div>
      )}

      {/* Workflow preview modal */}
      <WorkflowPreviewModal
        isOpen={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setSelectedWorkflow(null);
        }}
        workflow={selectedWorkflow}
        onEdit={handleEditWorkflow}
        hasEditPermission={hasConfigWrite}
        activeUsers={activeUsers}
        teams={teams}
      />

      {/* Create workflow modal */}
      <CreateWorkflowModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Bulk delete confirmation modal */}
      <BulkDeleteWorkflowsModal
        isOpen={bulkDeleteModalOpen}
        onClose={() => {
          setBulkDeleteModalOpen(false);
          setWorkflowsForBulkDelete([]);
        }}
        onConfirm={confirmBulkDelete}
        workflows={workflowsForBulkDelete}
        loading={bulkDeleteLoading}
      />

      {/* Bulk toggle active confirmation modal */}
      <BulkToggleActiveModal
        isOpen={bulkToggleModalOpen}
        onClose={() => {
          setBulkToggleModalOpen(false);
          setWorkflowsForBulkToggle([]);
        }}
        onConfirm={confirmBulkToggle}
        workflows={workflowsForBulkToggle}
        loading={bulkToggleLoading}
        activating={bulkToggleActivating}
      />
    </div>
  );
};

export default WorkflowViewController;
